<template>
  <div class="chart-type-selector" :class="{ 'dark': isDark }" ref="selectorRoot">
    <button 
      class="cts-btn" 
      @click.stop="toggleDropdown"
      :class="{ 'active': isOpen }"
      title="Chart Type"
      ref="btnRef"
    >
      <svg class="cts-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" v-html="currentIcon"></svg>
      <svg class="cts-arrow" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>
    
    <transition name="cts-fade">
      <div v-if="isOpen" class="cts-dropdown" :class="{ 'dark': isDark }" :style="dropdownStyle">
        <div 
          v-for="chartType in chartTypes" 
          :key="chartType.id"
          class="cts-item"
          :class="{ 'cts-item-active': currentChartType.id === chartType.id }"
          @click.stop="selectChartType(chartType)"
        >
          <svg class="cts-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" v-html="getIcon(chartType.id)"></svg>
          <span class="cts-item-label">{{ chartType.label }}</span>
          <svg v-if="currentChartType.id === chartType.id" class="cts-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
      </div>
    </transition>
    
    <div v-if="isOpen" class="cts-overlay" @click.stop="closeDropdown"></div>
  </div>
</template>

<script>
const ICONS = {
  'bars': '<line x1="6" y1="4" x2="6" y2="20"/><line x1="4" y1="8" x2="6" y2="8"/><line x1="6" y1="16" x2="8" y2="16"/><line x1="12" y1="6" x2="12" y2="18"/><line x1="10" y1="10" x2="12" y2="10"/><line x1="12" y1="14" x2="14" y2="14"/><line x1="18" y1="3" x2="18" y2="21"/><line x1="16" y1="7" x2="18" y2="7"/><line x1="18" y1="17" x2="20" y2="17"/>',
  'candles': '<rect x="5" y="8" width="3" height="8" fill="currentColor"/><line x1="6.5" y1="4" x2="6.5" y2="8"/><line x1="6.5" y1="16" x2="6.5" y2="20"/><rect x="10.5" y="6" width="3" height="10"/><line x1="12" y1="3" x2="12" y2="6"/><line x1="12" y1="16" x2="12" y2="21"/><rect x="16" y="9" width="3" height="7" fill="currentColor"/><line x1="17.5" y1="5" x2="17.5" y2="9"/><line x1="17.5" y1="16" x2="17.5" y2="19"/>',
  'hollow_candles': '<rect x="5" y="8" width="3" height="8"/><line x1="6.5" y1="4" x2="6.5" y2="8"/><line x1="6.5" y1="16" x2="6.5" y2="20"/><rect x="10.5" y="6" width="3" height="10"/><line x1="12" y1="3" x2="12" y2="6"/><line x1="12" y1="16" x2="12" y2="21"/><rect x="16" y="9" width="3" height="7"/><line x1="17.5" y1="5" x2="17.5" y2="9"/><line x1="17.5" y1="16" x2="17.5" y2="19"/>',
  'volume_candles': '<rect x="4" y="10" width="5" height="8" fill="currentColor"/><line x1="6.5" y1="6" x2="6.5" y2="10"/><line x1="6.5" y1="18" x2="6.5" y2="20"/><rect x="10" y="7" width="4" height="11"/><line x1="12" y1="4" x2="12" y2="7"/><line x1="12" y1="18" x2="12" y2="21"/><rect x="15" y="9" width="6" height="9" fill="currentColor"/><line x1="18" y1="5" x2="18" y2="9"/><line x1="18" y1="18" x2="18" y2="20"/>',
  'line': '<polyline points="3 17 8 11 13 14 17 7 21 10"/>',
  'line_markers': '<polyline points="3 17 8 11 13 14 17 7 21 10"/><circle cx="8" cy="11" r="2"/><circle cx="13" cy="14" r="2"/><circle cx="17" cy="7" r="2"/>',
  'step_line': '<polyline points="3 16 7 16 7 10 12 10 12 14 17 14 17 8 21 8"/>',
  'area': '<path d="M3 17L8 11L13 14L17 7L21 10V21H3Z" fill="currentColor" opacity="0.2"/><polyline points="3 17 8 11 13 14 17 7 21 10"/>',
  'hlc_area': '<path d="M3 8L8 6L13 9L17 5L21 7V17L17 19L13 15L8 18L3 16Z" fill="currentColor" opacity="0.2"/><polyline points="3 8 8 6 13 9 17 5 21 7"/><polyline points="3 16 8 18 13 15 17 19 21 17"/>',
  'baseline': '<line x1="2" y1="12" x2="22" y2="12" stroke-dasharray="3 3"/><polyline points="3 15 6 10 9 13 12 8 15 11 18 9 21 14"/>',
  'columns': '<rect x="3" y="12" width="3" height="9" fill="currentColor"/><rect x="8" y="8" width="3" height="13"/><rect x="13" y="10" width="3" height="11" fill="currentColor"/><rect x="18" y="5" width="3" height="16"/>',
  'high_low': '<line x1="6" y1="4" x2="6" y2="18"/><line x1="4" y1="4" x2="8" y2="4"/><line x1="4" y1="18" x2="8" y2="18"/><line x1="12" y1="6" x2="12" y2="16"/><line x1="10" y1="6" x2="14" y2="6"/><line x1="10" y1="16" x2="14" y2="16"/><line x1="18" y1="3" x2="18" y2="20"/><line x1="16" y1="3" x2="20" y2="3"/><line x1="16" y1="20" x2="20" y2="20"/>',
  'heikin_ashi': '<rect x="5" y="8" width="3" height="8" rx="1" fill="currentColor"/><line x1="6.5" y1="4" x2="6.5" y2="8"/><line x1="6.5" y1="16" x2="6.5" y2="20"/><rect x="10.5" y="5" width="3" height="11" rx="1" fill="currentColor"/><line x1="12" y1="3" x2="12" y2="5"/><line x1="12" y1="16" x2="12" y2="19"/><rect x="16" y="7" width="3" height="9" rx="1" fill="currentColor"/><line x1="17.5" y1="4" x2="17.5" y2="7"/><line x1="17.5" y1="16" x2="17.5" y2="20"/>',
  'renko': '<rect x="4" y="14" width="5" height="5" fill="currentColor"/><rect x="9" y="9" width="5" height="5" fill="currentColor"/><rect x="14" y="4" width="5" height="5" fill="currentColor"/><rect x="14" y="14" width="5" height="5"/>',
  'line_break': '<rect x="4" y="14" width="4" height="6" fill="currentColor"/><rect x="8" y="10" width="4" height="10" fill="currentColor"/><rect x="12" y="4" width="4" height="6"/><rect x="16" y="8" width="4" height="8"/>',
  'kagi': '<polyline points="5 18 5 8 9 8 9 16 13 16 13 6 17 6 17 14 21 14 21 4" fill="none"/>',
  'point_and_figure': '<line x1="4" y1="6" x2="8" y2="12"/><line x1="8" y1="6" x2="4" y2="12"/><line x1="10" y1="10" x2="14" y2="16"/><line x1="14" y1="10" x2="10" y2="16"/><circle cx="17" cy="8" r="3" fill="none"/><circle cx="17" cy="16" r="3" fill="none"/>',
  'range': '<rect x="3" y="6" width="5" height="12" fill="currentColor"/><line x1="5.5" y1="4" x2="5.5" y2="6"/><line x1="5.5" y1="18" x2="5.5" y2="20"/><rect x="10" y="8" width="5" height="10"/><line x1="12.5" y1="5" x2="12.5" y2="8"/><line x1="12.5" y1="18" x2="12.5" y2="21"/><rect x="17" y="4" width="5" height="14" fill="currentColor"/><line x1="19.5" y1="2" x2="19.5" y2="4"/><line x1="19.5" y1="18" x2="19.5" y2="21"/>'
}

export default {
  name: 'ChartTypeSelector',
  props: {
    isDark: { type: Boolean, default: false },
    currentType: { type: String, default: 'candles' }
  },
  data() {
    return {
      isOpen: false,
      dropdownStyle: {},
      chartTypes: [
        { id: 'bars', label: 'Bars' },
        { id: 'candles', label: 'TradingCandle Black Candles' },
        { id: 'standard_candles', label: 'Standard Candles' },
        { id: 'hollow_candles', label: 'Hollow Candles' },
        { id: 'volume_candles', label: 'Volume Candles' },
        { id: 'line', label: 'Line' },
        { id: 'line_markers', label: 'Line with Markers' },
        { id: 'step_line', label: 'Step Line' },
        { id: 'area', label: 'Area' },
        { id: 'hlc_area', label: 'HLC Area' },
        { id: 'baseline', label: 'Baseline' },
        { id: 'columns', label: 'Columns' },
        { id: 'high_low', label: 'High-Low' },
        { id: 'heikin_ashi', label: 'Heikin Ashi' },
        { id: 'renko', label: 'Renko' },
        { id: 'line_break', label: 'Line Break' },
        { id: 'kagi', label: 'Kagi' },
        { id: 'point_and_figure', label: 'Point & Figure' },
        { id: 'range', label: 'Range' }
      ]
    }
  },
  computed: {
    currentChartType() {
      return this.chartTypes.find(type => type.id === this.currentType) || this.chartTypes[1]
    },
    currentIcon() {
      return ICONS[this.currentType] || ICONS['candles']
    }
  },
  methods: {
    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown()
      } else {
        this.openDropdown()
      }
    },
    openDropdown() {
      // Calculate fixed position based on button location
      const btn = this.$refs.btnRef
      if (btn) {
        const rect = btn.getBoundingClientRect()
        this.dropdownStyle = {
          position: 'fixed',
          top: (rect.bottom + 4) + 'px',
          left: rect.left + 'px'
        }
      }
      this.isOpen = true
    },
    closeDropdown() {
      this.isOpen = false
    },
    selectChartType(chartType) {
      this.$emit('chart-type-changed', chartType.id)
      this.closeDropdown()
    },
    getIcon(id) {
      return ICONS[id] || ICONS['candles']
    }
  },
  mounted() {
    this._outsideClickHandler = (e) => {
      if (this.isOpen && !this.$el.contains(e.target)) {
        this.closeDropdown()
      }
    }
    document.addEventListener('click', this._outsideClickHandler)
  },
  beforeDestroy() {
    document.removeEventListener('click', this._outsideClickHandler)
  }
}
</script>

<style scoped>
.chart-type-selector {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.cts-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 6px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #131722;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.cts-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #2962ff;
}
.cts-btn.active {
  background: rgba(41, 98, 255, 0.1);
  color: #2962ff;
}
.cts-icon { flex-shrink: 0; }
.cts-arrow {
  flex-shrink: 0;
  opacity: 0.5;
  transition: transform 0.2s ease;
}
.cts-btn.active .cts-arrow { transform: rotate(180deg); }

/* Dropdown uses fixed positioning to escape overflow */
.cts-dropdown {
  position: fixed;
  background: #ffffff;
  border: 1px solid #e0e3eb;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  min-width: 190px;
  max-height: 380px;
  overflow-y: auto;
  padding: 4px 0;
}
.cts-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #131722;
  transition: background 0.1s ease;
  white-space: nowrap;
}
.cts-item:hover { background: rgba(0, 0, 0, 0.04); }
.cts-item-active {
  background: rgba(41, 98, 255, 0.08);
  color: #2962ff;
  font-weight: 600;
}
.cts-item-active:hover { background: rgba(41, 98, 255, 0.12); }
.cts-item-icon { flex-shrink: 0; opacity: 0.6; }
.cts-item-active .cts-item-icon { opacity: 1; color: #2962ff; }
.cts-item-label { flex: 1; }
.cts-check { flex-shrink: 0; color: #2962ff; }

.cts-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 99998;
}

.cts-fade-enter-active, .cts-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.cts-fade-enter, .cts-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Dark theme */
.dark .cts-btn { color: #d1d4dc; }
.dark .cts-btn:hover { background: rgba(255, 255, 255, 0.08); color: #5b9cf6; }
.dark .cts-btn.active { background: rgba(91, 156, 246, 0.15); color: #5b9cf6; }

.cts-dropdown.dark {
  background: #1e2436;
  border-color: rgba(55, 59, 75, 0.8);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}
.cts-dropdown.dark .cts-item { color: #d1d4dc; }
.cts-dropdown.dark .cts-item:hover { background: rgba(255, 255, 255, 0.06); }
.cts-dropdown.dark .cts-item-active { background: rgba(91, 156, 246, 0.12); color: #5b9cf6; }
.cts-dropdown.dark .cts-item-active:hover { background: rgba(91, 156, 246, 0.18); }
.cts-dropdown.dark .cts-item-active .cts-item-icon { color: #5b9cf6; }
.cts-dropdown.dark .cts-check { color: #5b9cf6; }

.cts-dropdown::-webkit-scrollbar { width: 5px; }
.cts-dropdown::-webkit-scrollbar-track { background: transparent; }
.cts-dropdown::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
.cts-dropdown.dark::-webkit-scrollbar-thumb { background: #3f4555; }
</style>
